import {
  createContext,
  useContext,
  useMemo,
  useCallback,
  ReactNode,
} from 'react';
import {
  createTheme,
  ThemeProvider as MuiThemeProvider,
} from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { colors } from './colors';
import { useApp } from '@context/AppContext';
import { useCompanyThemeApi } from '@api/useCompanyThemeApi';
import {
  deriveAccessibleTextColors,
  generateColorScale,
  getContrastText,
  type ColorScale,
} from '@utils/colorScale';

interface ThemeContextType {
  themeColor: string;
  backgroundColor: string;
  isCustomTheme: boolean;
  setThemeColor: (color: string) => void;
  setBackgroundColor: (color: string) => void;
}

const ThemeContext = createContext<ThemeContextType>({} as ThemeContextType);

declare module '@mui/material/styles' {
  interface Theme {
    elevations: {
      header: number;
      card: number;
      dialog: number;
      tooltip: number;
    };
  }
  interface ThemeOptions {
    elevations?: {
      header: number;
      card: number;
      dialog: number;
      tooltip: number;
    };
  }
  interface TypeBackground {
    light: string;
    dark: string;
    footer: string;
    white: string;
  }
  interface TypeText {
    heading: string;
    body: string;
    muted: string;
    caption: string;
    link: string;
    success: string;
    error: string;
    warning: string;
    onCanvas: string;
    onCanvasSecondary: string;
  }
  interface TypographyVariants {
    pageTitle: React.CSSProperties;
    label: React.CSSProperties;
    value: React.CSSProperties;
    accountName: React.CSSProperties;
    propertyAddress: React.CSSProperties;
    notification: React.CSSProperties;
    link: React.CSSProperties;
    error: React.CSSProperties;
    success: React.CSSProperties;
    tableHeader: React.CSSProperties;
    tableCell: React.CSSProperties;
    badge: React.CSSProperties;
  }
  interface TypographyVariantsOptions {
    pageTitle?: React.CSSProperties;
    label?: React.CSSProperties;
    value?: React.CSSProperties;
    accountName?: React.CSSProperties;
    propertyAddress?: React.CSSProperties;
    notification?: React.CSSProperties;
    link?: React.CSSProperties;
    error?: React.CSSProperties;
    success?: React.CSSProperties;
    tableHeader?: React.CSSProperties;
    tableCell?: React.CSSProperties;
    badge?: React.CSSProperties;
  }
}

declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    pageTitle: true;
    label: true;
    value: true;
    accountName: true;
    propertyAddress: true;
    notification: true;
    link: true;
    error: true;
    success: true;
    tableHeader: true;
    tableCell: true;
    badge: true;
  }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const { companyId } = useApp();
  const { data: companyTheme, isLoading: isThemeLoading } =
    useCompanyThemeApi(companyId);

  // Setters exposed for future admin preview capability
  const setThemeColor = useCallback((_color: string) => {}, []);
  const setBackgroundColor = useCallback((_color: string) => {}, []);

  const themeScale = useMemo<ColorScale | null>(
    () =>
      companyTheme?.themeColor
        ? generateColorScale(companyTheme.themeColor)
        : null,
    [companyTheme?.themeColor]
  );

  const bgScale = useMemo<ColorScale | null>(
    () =>
      companyTheme?.themeBackgroundColor
        ? generateColorScale(companyTheme.themeBackgroundColor)
        : null,
    [companyTheme?.themeBackgroundColor]
  );

  const isCustomTheme = !!(themeScale || bgScale);

  const p = useMemo(
    () => ({
      main: themeScale?.[500] ?? colors.primary.main,
      light: themeScale?.[50] ?? colors.primary.light,
      dark: themeScale?.[700] ?? colors.primary.dark,
      contrastText: themeScale
        ? getContrastText(themeScale[500])
        : colors.primary.contrastText,
      containedContrastText: getContrastText(
        themeScale?.[600] ?? colors.teal[600]
      ),
      containedHoverContrastText: getContrastText(
        themeScale?.[700] ?? colors.teal[700]
      ),
      50: themeScale?.[50] ?? colors.teal[100],
      100: themeScale?.[100] ?? colors.teal[100],
      200: themeScale?.[200] ?? colors.teal[200],
      300: themeScale?.[300] ?? colors.teal[300],
      400: themeScale?.[400] ?? colors.teal[400],
      500: themeScale?.[500] ?? colors.teal[500],
      600: themeScale?.[600] ?? colors.teal[600],
      700: themeScale?.[700] ?? colors.teal[700],
    }),
    [themeScale]
  );

  const bg = useMemo(
    () => ({
      default: companyTheme?.themeBackgroundColor ?? colors.teal[200],
      light: bgScale?.[50] ?? colors.background.default,
      dark: bgScale?.[300] ?? colors.background.footer,
      paper: colors.background.paper,
      footer: bgScale?.[200] ?? colors.background.footer,
      white: colors.grey[100],
    }),
    [companyTheme?.themeBackgroundColor, bgScale]
  );

  const textPalette = useMemo(
    () => deriveAccessibleTextColors(bg.paper, p.main),
    [bg.paper, p.main]
  );

  const canvasText = useMemo(
    () => deriveAccessibleTextColors(bg.default, p.main),
    [bg.default, p.main]
  );

  // TODO: Remove this debug log
  console.log('[Theme Debug]', {
    apiResponse: companyTheme,
    themeColor: companyTheme?.themeColor,
    backgroundColor: companyTheme?.themeBackgroundColor,
    themeScaleGenerated: !!themeScale,
    resolvedPrimary: {
      main: p.main,
      light: p.light,
      dark: p.dark,
      contrastText: p.contrastText,
    },
    resolvedBackground: bg,
  });

  const theme = createTheme({
    breakpoints: {
      values: {
        xs: 0,
        sm: 450,
        md: 768,
        lg: 1024,
        xl: 1440,
      },
    },
    palette: {
      primary: {
        main: p.main,
        light: p.light,
        dark: p.dark,
        contrastText: p.contrastText,
      },
      secondary: {
        main: colors.secondary.main,
        light: colors.secondary.light,
        dark: colors.secondary.dark,
      },
      background: {
        default: bg.default,
        light: bg.light,
        dark: bg.dark,
        paper: bg.paper,
        footer: bg.footer,
        white: bg.white,
      },
      text: {
        primary: textPalette.primary,
        secondary: textPalette.secondary,
        heading: textPalette.heading,
        body: textPalette.body,
        muted: textPalette.muted,
        caption: textPalette.caption,
        link: textPalette.link,
        success: textPalette.success,
        error: textPalette.error,
        warning: textPalette.warning,
        onCanvas: canvasText.primary,
        onCanvasSecondary: canvasText.secondary,
      },
      action: {
        hover: bgScale?.[200] ?? colors.action.hover,
        selected: bgScale?.[100] ?? colors.action.selected,
      },
      grey: colors.grey,
      teal: themeScale
        ? {
            100: p[100],
            200: p[200],
            300: p[300],
            400: p[400],
            500: p[500],
            600: p[600],
            700: p[700],
          }
        : colors.teal,
      blue: colors.blue,
      green: colors.green,
      red: colors.red,
      purple: colors.purple,
      orange: colors.orange,
    },
    typography: {
      fontFamily: 'Epilogue, Arial, sans-serif',
      pageTitle: {
        fontWeight: 500,
        fontSize: '1.75rem',
        fontFamily: 'Epilogue, Arial, sans-serif',
        color: canvasText.primary,
        lineHeight: '150%',
      },
      h1: {
        fontWeight: 500,
        fontSize: '1.75rem',
        fontFamily: 'Epilogue, Arial, sans-serif',
        color: textPalette.primary,
        lineHeight: '150%',
      },
      h2: {
        fontWeight: 500,
        fontSize: '1.375rem',
        fontFamily: 'Epilogue, sans-serif',
        lineHeight: '150%',
        color: textPalette.primary,
      },
      h3: {
        fontWeight: 600,
        fontSize: '1.25rem',
        fontFamily: 'Epilogue, sans-serif',
        lineHeight: '150%',
        color: textPalette.primary,
      },
      h6: {
        fontWeight: 500,
        fontStyle: 'normal',
        fontSize: '1.125rem',
        fontFamily: 'Epilogue, sans-serif',
        lineHeight: '150%',
        color: textPalette.primary,
      },
      subtitle1: {
        fontSize: '1rem',
        color: textPalette.secondary,
      },
      subtitle2: {
        fontFamily: 'Epilogue',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '0.875rem',
        lineHeight: '150%',
        color: textPalette.secondary,
      },
      subtitle3: {
        fontFamily: 'Epilogue',
        fontStyle: 'normal',
        fontWeight: 500,
        fontSize: '0.8125rem',
        lineHeight: '150%',
        color: textPalette.secondary,
      },
      body1: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.primary,
        textOverflow: 'ellipsis',
      },
      body2: {
        fontSize: '0.875rem',
        fontWeight: 'medium',
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.primary,
      },
      body3: {
        fontSize: '0.75rem',
        fontWeight: 'medium',
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.primary,
      },
      caption: {
        fontSize: '0.8125rem',
        fontWeight: 'medium',
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.caption,
      },
      // Common variants for frequently used patterns
      label: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: 'var(--mid-gray)',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },
      value: {
        fontSize: '1.125rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: 'var(--dark-gray)',
      },
      accountName: {
        fontSize: '1.375rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: 'var(--dark-gray)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        minWidth: 0,
      },
      propertyAddress: {
        fontSize: '1.0625rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: 'var(--mid-gray)',
      },
      notification: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: 'var(--mid-gray)',
      },
      link: {
        fontSize: '1rem',
        fontWeight: 600,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.link,
        textDecoration: 'none',
      },

      error: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.error,
        textAlign: 'center',
      },
      success: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.success,
        textAlign: 'center',
      },
      tableHeader: {
        fontSize: '1rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.muted,
      },
      tableCell: {
        fontSize: '1rem',
        fontWeight: 400,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: textPalette.primary,
      },
      badge: {
        fontSize: '0.625rem',
        fontWeight: 500,
        lineHeight: '150%',
        fontFamily: 'Epilogue',
        color: 'var(--teal-blue)',
        whiteSpace: 'nowrap',
      },
    },
    components: {
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: colors.background.paper,
            boxShadow: '0px 1px 3px rgba(0, 0, 0, 0.1)',
            borderRadius: 0,
            width: '100%',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: '10px',
            boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            '&.MuiPaper-elevation0': {
              boxShadow: 'none',
            },
            '&.MuiPaper-elevation1': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
            },
            '&.MuiPaper-elevation2': {
              boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.1)',
            },
            '&.documents-container': {
              p: { xs: 1, sm: 2 },
              pl: { xs: 2, sm: 4, md: 6, lg: 7 },
              pr: { xs: 2, sm: 3, md: 4, lg: 4 },
              mt: { xs: 4, sm: 6 },
              mb: 3,
            },
            '&.documents-header': {
              display: 'flex',
              flexDirection: { xs: 'row', sm: 'row' },
              justifyContent: 'space-between',
              alignItems: { xs: 'flex-start', sm: 'center' },
              mb: 3,
              gap: { xs: 1, sm: 0 },
            },
          },
        },
      },
      MuiLink: {
        styleOverrides: {
          root: ({ theme }) => ({
            color: theme.palette.text.link,
            textDecoration: 'none',
            fontWeight: 'bolder',
            '&:hover': { textDecoration: 'underline' },
            '&.Mui-disabled': { color: colors.grey[400] },
          }),
        },
      },
      MuiTypography: {
        styleOverrides: {
          root: {
            '&.documents-title': {
              fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
              fontWeight: 500,
              lineHeight: 1.2,
            },
            '&.documents-last-updated': {
              fontSize: { xs: '0.75rem', sm: '0.875rem' },
              pr: { xs: 0, sm: 1 },
              mt: { xs: 0.5, sm: 0 },
            },
            '&.documents-accordion': {
              backgroundColor: 'background.white',
              borderRadius: '16px !important',
              '&:before': {
                display: 'none',
              },
              border: 'none',
              boxShadow: 'none',
              mb: 2,
              width: '100%',
              overflow: 'hidden',
              outline: 'none',
              '&:focus': {
                outline: 'none',
              },
              '&:hover': {
                outline: 'none',
              },
              '& .MuiAccordionSummary-root': {
                outline: 'none !important',
                pl: { xs: 2, sm: 4, md: 6, lg: 7 },
                pr: { xs: 2, sm: 3, md: 4, lg: 4 },
                '&:focus': {
                  outline: 'none !important',
                },
                '&:hover': {
                  outline: 'none !important',
                },
                '&.Mui-focusVisible': {
                  outline: 'none !important',
                },
              },
              '& .MuiAccordionDetails-root': {
                outline: 'none !important',
              },
              '& .MuiButtonBase-root': {
                outline: 'none !important',
              },
            },
            '&.documents-loading': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            },
            '&.documents-error': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
            },
            '&.documents-empty': {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              py: 4,
              color: 'var(--mid-gray)',
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            '& .MuiInputBase-input': {
              textAlign: 'left',
              height: '20px',
            },
          },
        },
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: p[500],
            },
            '& input:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 1000px ${p.light} inset`,
              WebkitTextFillColor: textPalette.primary,
            },
            '& input:-moz-autofill': {
              boxShadow: `0 0 0 1000px ${p.light} inset`,
              MozBoxShadow: `0 0 0 1000px ${p.light} inset`,
              textFillColor: textPalette.primary,
            },
          },
        },
      },
      MuiSelect: {
        styleOverrides: {
          root: {
            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
              borderColor: p[500],
            },
            '&:-webkit-autofill': {
              WebkitBoxShadow: `0 0 0 1000px ${p.light} inset`,
              WebkitTextFillColor: textPalette.primary,
            },
            '&:-moz-autofill': {
              boxShadow: `0 0 0 1000px ${p.light} inset`,
              MozBoxShadow: `0 0 0 1000px ${p.light} inset`,
              textFillColor: textPalette.primary,
            },
          },
        },
      },
      MuiMenuItem: {
        styleOverrides: {
          root: {
            '&:hover': {
              backgroundColor: p.light,
            },
          },
        },
      },
      MuiFormLabel: {
        styleOverrides: {
          root: {
            fontWeight: 'medium',
            fontSize: '0.875rem',
            lineHeight: '150%',
            color: textPalette.primary,
            fontFamily: 'Epilogue',
            '&.MuiInputLabel-shrink': {
              color: p[500],
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderBottom: '1px solid rgba(224, 224, 224, 0.5)',
            padding: '8px 16px',
            lineHeight: '1.5 !important',
            maxHeight: '48px !important',
          },
          head: {
            fontWeight: 600,
            backgroundColor: p.light,
          },
          footer: {
            fontSize: 'small',
            fontWeight: 'bold',
            color: bgScale?.[200] ?? colors.action.hover,
          },
        },
      },

      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            borderRadius: '8px',
            fontWeight: 500,
            '&.back-button': {
              display: 'flex',
              width: '123px',
              height: '39px',
              padding: '6px 20px 6px 12px',
              borderRadius: '8px',
              border: `1.5px solid ${p[600]}`,
              background: p[200],
              color: p[600],
              textDecoration: 'none',
              justifyContent: 'center',
              fontFamily: 'Epilogue',
              fontSize: '18px',
              fontStyle: 'normal',
              lineHeight: '150%',
              fontWeight: 500,
              cursor: 'pointer',
              textTransform: 'none',
              '&:hover': {
                backgroundColor: p[600],
                border: `1.5px solid ${p[600]}`,
                color: bg.paper,
              },
              '&:focus-visible': {
                backgroundColor: p[600],
                border: `1.5px solid ${p[600]}`,
                color: bg.paper,
                outline: `2px solid ${p[600]}`,
                outlineOffset: '2px',
              },
            },
          },
          contained: {
            boxShadow: 'none',
            '&:hover': {
              boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.1)',
            },
            '&.MuiButton-containedPrimary': {
              backgroundColor: p[600],
              color: p.containedContrastText,
              border: `2px solid ${p[600]}`,
              '&:hover': {
                backgroundColor: p[700],
                color: p.containedHoverContrastText,
                border: `2px solid ${p[700]}`,
              },
              '&:active': {
                backgroundColor: p[700],
                color: p.containedHoverContrastText,
                border: `2px solid ${p[700]}`,
              },
              '&:focus-visible': {
                backgroundColor: p[700],
                color: p.containedHoverContrastText,
                border: `2px solid ${p[700]}`,
              },
              '&.Mui-disabled': {
                color: bg.paper,
                borderColor: colors.grey[400],
                backgroundColor: colors.grey[400],
              },
            },
            '&.MuiButton-containedSecondary': {
              backgroundColor: bg.paper,
              color: p[600],
              border: `2px solid ${p[600]}`,
              '&:hover': {
                backgroundColor: bg.paper,
                color: p[700],
                border: `2px solid ${p[700]}`,
              },
              '&:active': {
                backgroundColor: bg.paper,
                color: p[700],
                border: `2px solid ${p[700]}`,
              },
              '&:focus-visible': {
                backgroundColor: bg.paper,
                color: p[600],
                border: `2px solid ${p[600]}`,
              },
              '&.Mui-disabled': {
                color: colors.grey[400],
                borderColor: colors.grey[400],
                backgroundColor: 'transparent',
              },
            },
            '&.filter-button': {
              fontFamily: 'Epilogue',
              fontSize: '1.125rem',
              fontWeight: 500,
              fontStyle: 'normal',
              lineHeight: '150%',
              borderRadius: '8px',
              padding: '12px 32px',
              border: 'none',
              cursor: 'pointer',
              alignSelf: 'stretch',
              textTransform: 'none',
              '&.filter-button-active': {
                backgroundColor: p[500],
                color: '#ffffff',
                borderColor: p[500],
                border: '1px solid',
                '&:hover': {
                  backgroundColor: p[600],
                  borderColor: p[600],
                },
              },
              '&.filter-button-inactive': {
                backgroundColor: '#f8f9fa',
                color: '#495057',
                borderColor: '#ced4da',
                border: '1px solid',
                '&:hover': {
                  backgroundColor: '#e9ecef',
                  borderColor: '#adb5bd',
                },
              },
            },
          },
          outlined: {
            '&.MuiButton-outlinedSecondary': {
              backgroundColor: 'transparent',
              color: p.dark,
              border: `1px solid ${p.dark}`,
              '&:hover': {
                backgroundColor: p.light,
                borderColor: p.dark,
                color: p.main,
              },
              '&:active': {
                backgroundColor: bgScale?.[200] ?? colors.action.hover,
                borderColor: p.dark,
                color: p.main,
              },
              '&:focus-visible': {
                backgroundColor: p.light,
                borderColor: p.dark,
                color: p.main,
                outline: `2px solid ${p.dark}`,
                outlineOffset: '2px',
              },
              '&.Mui-disabled': {
                color: colors.grey[400],
                borderColor: colors.grey[400],
                backgroundColor: 'transparent',
              },
            },
          },
        },
      },
      MuiAccordion: {
        styleOverrides: {
          root: {
            backgroundColor: 'background.white',
            borderRadius: '16px !important',
            '&:before': {
              display: 'none',
            },
            border: 'none',
            boxShadow: 'none',
            mb: 2,
            width: '100%',
            overflow: 'hidden',
            outline: 'none',
            '&:focus': {
              outline: 'none',
            },
            '&:hover': {
              outline: 'none',
            },
            '& .MuiAccordionSummary-root': {
              outline: 'none !important',
              '&:focus': {
                outline: 'none !important',
              },
              '&:hover': {
                outline: 'none !important',
              },
              '&.Mui-focusVisible': {
                outline: 'none !important',
              },
            },
            '& .MuiAccordionDetails-root': {
              outline: 'none !important',
            },
            '& .MuiButtonBase-root': {
              outline: 'none !important',
              '&:focus': {
                outline: 'none !important',
              },
              '&:hover': {
                outline: 'none !important',
              },
            },
            '& button': {
              outline: 'none !important',
              '&:focus': {
                outline: 'none !important',
              },
              '&:hover': {
                outline: 'none !important',
              },
            },
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          root: {
            backgroundColor: 'transparent',
            '&:hover': {
              backgroundColor: 'transparent',
            },
            '&.Mui-expanded': {
              backgroundColor: 'transparent',
              minHeight: 'unset',
            },
            borderRadius: 'var(--bs-border-radius)',
            '& .MuiAccordionSummary-expandIconWrapper': {
              display: 'none',
            },
            pl: 6,
            pr: 5,
            mt: 2,
            minHeight: { xs: 'unset', md: 'unset' },
            width: '100%',
            '& .MuiAccordionSummary-content': {
              margin: 0,
              width: '100%',
            },
          } as any,
        },
      },
      MuiAccordionDetails: {
        styleOverrides: {
          root: {
            p: 2,
            backgroundColor: 'background.white',
          },
        },
      },
    },
    elevations: {
      header: 0,
      card: 1,
      dialog: 2,
      tooltip: 0,
    },
  });

  if (companyId && isThemeLoading) {
    return null;
  }

  return (
    <ThemeContext.Provider
      value={{
        themeColor: p.main,
        backgroundColor: bg.default,
        isCustomTheme,
        setThemeColor,
        setBackgroundColor,
      }}
    >
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
